import moment from "moment-timezone";

// Return HHmm in UTC as string (e.g. "1345")
export const getUtcHHmm = () => {
  const d = new Date();
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `${hh}${mm}`;
};

// Compute key session UTC open/close times (moment-timezone handles DST automatically)
export const computeSessionTimes = () => {
  // Helper: given a time zone and local session time, return HHmm in UTC
  const sessionUtc = (tz, localHour, localMinute = 0) => {
    // Get current UTC time and convert to target timezone to find today's date
    const nowUtc = moment.utc();
    const targetTzNow = nowUtc.clone().tz(tz);
    
    // Set the local hour/minute for today in the target timezone
    const targetLocalTime = targetTzNow.clone()
      .hour(localHour)
      .minute(localMinute)
      .second(0)
      .millisecond(0);
    
    // Convert back to UTC to get the UTC time
    const utcMoment = targetLocalTime.clone().utc();
    const hh = String(utcMoment.hour()).padStart(2, "0");
    const mm = String(utcMoment.minute()).padStart(2, "0");
    return `${hh}${mm}`;
  };

  // Define sessions in their LOCAL market times
  // Sydney: 07:00–16:00 local (matches BabyPips forex market hours)
  const sydney_open = sessionUtc("Australia/Sydney", 7, 0);
  const sydney_close = sessionUtc("Australia/Sydney", 16, 0);

  // Market open time (when trading actually starts - DST adjusted)
  // Sydney: 09:00 local during DST (different from session start)
  const sydney_market_open = sessionUtc("Australia/Sydney", 9, 0);

  // Tokyo: 09:00–18:00 local
  const tokyo_open = sessionUtc("Asia/Tokyo", 9, 0);
  const tokyo_close = sessionUtc("Asia/Tokyo", 18, 0);

  // London: 08:00–17:00 local
  const london_open = sessionUtc("Europe/London", 8, 0);
  const london_close = sessionUtc("Europe/London", 17, 0);

  // New York: 08:00–17:00 local
  const new_york_open = sessionUtc("America/New_York", 8, 0);
  const new_york_close = sessionUtc("America/New_York", 17, 0);

  return {
    sydney_open,
    sydney_close,
    sydney_market_open,
    tokyo_open,
    tokyo_close,
    london_open,
    london_close,
    new_york_open,
    new_york_close,
  };
};

export const addUtcMinutes = (date, minutes) =>
  new Date(date.getTime() + minutes * 60000);

export const hhmmToUtcDate = (baseUtcDate, hhmm) => {
  let hh = parseInt(hhmm.slice(0, 2), 10);
  let mm = parseInt(hhmm.slice(2, 4), 10);
  let dayAdjust = 0;
  if (hh === 24) {
    hh = 0;
    dayAdjust = 1;
  }
  
  // Create the target date for today in UTC
  const target = new Date(
    Date.UTC(
      baseUtcDate.getUTCFullYear(),
      baseUtcDate.getUTCMonth(),
      baseUtcDate.getUTCDate() + dayAdjust,
      hh,
      mm,
      0,
      0
    )
  );
  
  return target;
};

export const msUntilUtcHHmm = (hhmm) => {
  const now = new Date();
  
  // Try yesterday, today, and tomorrow to find the next valid occurrence
  // This handles timezone boundaries where a time might refer to a different day
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const candidates = [
    hhmmToUtcDate(yesterday, hhmm),
    hhmmToUtcDate(now, hhmm),
    addUtcMinutes(hhmmToUtcDate(now, hhmm), 24 * 60)
  ];
  
  // Find the first future occurrence
  let target = candidates.find(t => t.getTime() > now.getTime());
  if (!target) {
    target = addUtcMinutes(hhmmToUtcDate(now, hhmm), 24 * 60);
  }
  
  // Skip Saturday if target lands on Saturday (UTC day 6)
  // Markets are closed all day Saturday, so skip to Sunday
  while (target.getUTCDay() === 6) {
    target = addUtcMinutes(target, 24 * 60);
  }
  
  return target.getTime() - now.getTime();
};

export const nextUtcDateForHHmm = (hhmm) => {
  const now = new Date();
  
  // Try yesterday, today, and tomorrow to find the next valid occurrence
  // This handles timezone boundaries where a time might refer to a different day
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const candidates = [
    hhmmToUtcDate(yesterday, hhmm),
    hhmmToUtcDate(now, hhmm),
    addUtcMinutes(hhmmToUtcDate(now, hhmm), 24 * 60)
  ];
  
  // Find the first future occurrence
  let target = candidates.find(t => t.getTime() > now.getTime());
  if (!target) {
    target = addUtcMinutes(hhmmToUtcDate(now, hhmm), 24 * 60);
  }
  
  // Skip Saturday if target lands on Saturday (UTC day 6)
  // Markets are closed all day Saturday, so skip to Sunday
  while (target.getUTCDay() === 6) {
    target = addUtcMinutes(target, 24 * 60);
  }
  
  return target;
};

export const formatLocalShort = (date) => {
  if (!date) return "--:--";
  if (!(date instanceof Date)) return "--:--";

  try {
    // Get the user's timezone from settings, fallback to browser detected timezone
    const storedTimezone = localStorage.getItem("economic-calendar-timezone");
    const userTz = (storedTimezone && storedTimezone !== "local") ? storedTimezone : Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Convert the UTC date to user's local time using moment-timezone
    const localMoment = moment(date).tz(userTz);
    const h = localMoment.hour();
    const m = localMoment.minute();
    return `${String(h).padStart(2, "0")}${String(m).padStart(2, "0")}`;
  } catch {
    // Fallback to native Date methods if moment fails
    const h = date.getHours();
    const m = date.getMinutes();
    return `${String(h).padStart(2, "0")}${String(m).padStart(2, "0")}`;
  }
};

export const buildMarketView = () => {
  const times = computeSessionTimes();
  const now = new Date();
  const utcHHmm = getUtcHHmm();

  const utcNum = parseInt(utcHHmm, 10);
  const utcDay = now.getUTCDay(); // 0 = Sunday, 6 = Saturday in UTC

  // Global weekend close window (UTC time):
  // - After New York close on Friday (UTC)
  // - All day Saturday (UTC)
  // - Before Sydney market opens on Sunday (UTC)
  const isWeekendClosed =
    (utcDay === 5 && utcNum >= parseInt(times.new_york_close, 10)) ||
    utcDay === 6 ||
    (utcDay === 0 && utcNum < parseInt(times.sydney_market_open, 10));

  const within = (start, end) => {
    const n = utcNum;
    const s = parseInt(start, 10);
    let e = parseInt(end, 10);
    const wraps = e < s;
    if (wraps) e += 2400;
    const nn = n < s && wraps ? n + 2400 : n;
    return nn >= s && nn < e;
  };

  const sydneyOpen =
    !isWeekendClosed && within(times.sydney_market_open, times.sydney_close);
  const tokyoOpen =
    !isWeekendClosed && within(times.tokyo_open, times.tokyo_close);
  const londonOpen =
    !isWeekendClosed && within(times.london_open, times.london_close);
  const newYorkOpen =
    !isWeekendClosed && within(times.new_york_open, times.new_york_close);

  const marketOpen = sydneyOpen || tokyoOpen || londonOpen || newYorkOpen;


  const countdowns = {
    cdSydneyOpen: msUntilUtcHHmm(times.sydney_market_open),
    cdSydneyClose: msUntilUtcHHmm(times.sydney_close),
    cdTokyoOpen: msUntilUtcHHmm(times.tokyo_open),
    cdTokyoClose: msUntilUtcHHmm(times.tokyo_close),
    cdLondonOpen: msUntilUtcHHmm(times.london_open),
    cdLondonClose: msUntilUtcHHmm(times.london_close),
    // If we're in the weekend-closed window, use sydney_market_open; otherwise use the
    // normal next New York open time.
    cdNewYorkOpen: isWeekendClosed
      ? msUntilUtcHHmm(times.sydney_market_open)
      : msUntilUtcHHmm(times.new_york_open),
  };

  // Detect if it's the last session of the week (Friday, only NYC open)
  const isEndOfWeek = 
    utcDay === 5 && 
    newYorkOpen && 
    !sydneyOpen && 
    !tokyoOpen && 
    !londonOpen;

  return {
    updatedAt: now.toISOString(),
    utcHHmm,
    times,
    status: {
      market: marketOpen,
      sydney: sydneyOpen,
      tokyo: tokyoOpen,
      london: londonOpen,
      newYork: newYorkOpen,
    },
    countdowns,
    isEndOfWeek,
  };
};

export const formatDuration = (ms) => {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

export const statusColorFor = (isOpen, msLeft) => {
  const ms = Math.max(0, msLeft || 0);
  const imminent = ms <= 10 * 60 * 1000;
  if (isOpen) {
    return imminent ? "#FFA000" : "#2E7D32"; // Orange warning when about to close
  }
  return imminent ? "#1976D2" : "#C62828"; // Blue warning when about to open
};

export const buildPills = (
  { status, times, isEndOfWeek },
  { compactClock, showLocalTimes, tick, theme }
) => {
  // Compute next timestamps and remaining time off the current clock each render
  // to avoid drift from throttled timers.
  const now = new Date();

  const defs = [
    {
      key: "sydney",
      label: compactClock ? "Syd" : "Sydney",
      nextTime: showLocalTimes
        ? nextUtcDateForHHmm(
            status.sydney ? times.sydney_close : times.sydney_market_open
          )
        : null,
      msLeft:
        (status.sydney
          ? nextUtcDateForHHmm(times.sydney_close)
          : nextUtcDateForHHmm(times.sydney_market_open)
        ).getTime() - now.getTime(),
    },
    {
      key: "tokyo",
      label: compactClock ? "Tky" : "Tokyo",
      nextTime: showLocalTimes
        ? nextUtcDateForHHmm(status.tokyo ? times.tokyo_close : times.tokyo_open)
        : null,
      msLeft:
        (status.tokyo
          ? nextUtcDateForHHmm(times.tokyo_close)
          : nextUtcDateForHHmm(times.tokyo_open)
        ).getTime() - now.getTime(),
    },
    {
      key: "london",
      label: compactClock ? "Lon" : "London",
      nextTime: showLocalTimes
        ? nextUtcDateForHHmm(
            status.london ? times.london_close : times.london_open
          )
        : null,
      msLeft:
        (status.london
          ? nextUtcDateForHHmm(times.london_close)
          : nextUtcDateForHHmm(times.london_open)
        ).getTime() - now.getTime(),
    },
    {
      key: "newYork",
      label: compactClock ? "NYC" : "New\u00A0York",
      nextTime: showLocalTimes
        ? nextUtcDateForHHmm(
            status.newYork ? times.new_york_close : times.new_york_open
          )
        : null,
      msLeft:
        (status.newYork
          ? nextUtcDateForHHmm(times.new_york_close)
          : nextUtcDateForHHmm(times.new_york_open)
        ).getTime() - now.getTime(),
    },
  ];

  let items = defs.map((d) => ({
    ...d,
    isOpen: status[d.key],
    dotColor: statusColorFor(status[d.key], d.msLeft),
  }));

  // If it's the end of the week (Friday, only NYC open), filter out closed sessions
  if (isEndOfWeek) {
    items = items.filter((item) => item.isOpen);
  }

  items.sort((a, b) => {
    if (a.isOpen !== b.isOpen) return a.isOpen ? -1 : 1;
    return a.msLeft - b.msLeft;
  });

  return items;
};